// Company Migration and Management Utilities
// Safe creation of new companies without affecting legacy company

import { ref, get, set, update, push } from "firebase/database";
import { getDatabaseInstance } from '../services/recruiterApi';
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { getAuthInstance } from '../services/recruiterApi';

// Create a new company with initial setup
export const createNewCompany = async (companyData) => {
  const database = getDatabaseInstance();
  
  try {
    // Generate unique company ID
    const timestamp = Date.now();
    const companyId = `company-${timestamp}`;
    
    const companyRef = ref(database, `companies/${companyId}`);
    
    const newCompany = {
      id: companyId,
      name: companyData.name,
      email: companyData.email,
      phone: companyData.phone,
      address: companyData.address,
      createdAt: new Date().toISOString(),
      isActive: true,
      settings: {
        timezone: companyData.timezone || 'America/Los_Angeles',
        workingHours: {
          start: companyData.workStart || '09:00',
          end: companyData.workEnd || '17:00'
        },
        features: {
          timeTracking: true,
          approvals: true,
          reporting: true
        },
        automaticPunches: {
          enabled: false,
          dailyDeduction: 30, // default 30 minutes per day
          applyToAllEmployees: true,
          excludedEmployees: [],
          effectiveDate: new Date().toISOString()
        },
        timeRounding: {
          enabled: false,
          roundingInterval: 15, // round to nearest 15 minutes
          effectiveDate: new Date().toISOString()
        }
      }
    };
    
    await set(companyRef, newCompany);
    
    console.log(`New company created: ${companyId} - ${companyData.name}`);
    return {
      companyId,
      company: newCompany
    };
    
  } catch (error) {
    console.error('Error creating new company:', error);
    throw error;
  }
};

// Create first admin user for a new company
export const createCompanyAdmin = async (companyId, adminData) => {
  const auth = getAuthInstance();
  const database = getDatabaseInstance();
  
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      adminData.email,
      adminData.password
    );
    
    const userId = userCredential.user.uid;
    
    // Update Firebase Auth profile
    await updateProfile(userCredential.user, {
      displayName: adminData.name
    });
    
    // Create user data in company structure
    const userRef = ref(database, `companies/${companyId}/users/${userId}`);
    const userData = {
      id: userId,
      name: adminData.name,
      email: adminData.email,
      role: 'admin', // Company admin role
      status: 'active',
      isCompanyAdmin: true,
      createdAt: new Date().toISOString()
    };
    
    await set(userRef, userData);
    
    // Create user-to-company mapping
    const mappingRef = ref(database, `userCompanyMappings/${userId}`);
    await set(mappingRef, {
      companyId: companyId,
      companyName: adminData.companyName,
      role: 'admin',
      isAdmin: true,
      joinedAt: new Date().toISOString()
    });
    
    console.log(`Company admin created: ${userId} for company ${companyId}`);
    return {
      userId,
      userData
    };
    
  } catch (error) {
    console.error('Error creating company admin:', error);
    throw error;
  }
};

// Add user to a specific company (for new companies only)
export const addUserToCompany = async (companyId, userData) => {
  const auth = getAuthInstance();
  const database = getDatabaseInstance();
  
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );
    
    const userId = userCredential.user.uid;
    
    // Update Firebase Auth profile
    await updateProfile(userCredential.user, {
      displayName: userData.name
    });
    
    // Create user data in company structure
    const userRef = ref(database, `companies/${companyId}/users/${userId}`);
    const newUserData = {
      id: userId,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      status: userData.status || 'active',
      qrCode: userData.qrCode,
      createdAt: new Date().toISOString()
    };
    
    await set(userRef, newUserData);
    
    // Get company info for mapping
    const companyRef = ref(database, `companies/${companyId}`);
    const companySnapshot = await get(companyRef);
    const companyName = companySnapshot.exists() ? companySnapshot.val().name : 'Unknown Company';
    
    // Create user-to-company mapping
    const mappingRef = ref(database, `userCompanyMappings/${userId}`);
    await set(mappingRef, {
      companyId: companyId,
      companyName: companyName,
      role: userData.role,
      isAdmin: false,
      joinedAt: new Date().toISOString()
    });
    
    console.log(`User added to company: ${userId} to ${companyId}`);
    return newUserData;
    
  } catch (error) {
    console.error('Error adding user to company:', error);
    throw error;
  }
};

// Get all companies (for admin dashboard)
export const getAllCompanies = async () => {
  const database = getDatabaseInstance();
  
  try {
    const companiesRef = ref(database, 'companies');
    const snapshot = await get(companiesRef);
    
    const companies = [];
    
    // Add legacy company first
    companies.push({
      id: 'legacy',
      name: 'Creative Personnel Technology',
      isLegacy: true,
      createdAt: '2024-01-01T00:00:00.000Z', // Placeholder
      isActive: true
    });
    
    // Add new companies
    if (snapshot.exists()) {
      const data = snapshot.val();
      Object.keys(data).forEach(companyId => {
        companies.push({
          id: companyId,
          ...data[companyId],
          isLegacy: false
        });
      });
    }
    
    return companies;
  } catch (error) {
    console.error('Error getting all companies:', error);
    return [{
      id: 'legacy',
      name: 'Creative Personnel Technology',
      isLegacy: true,
      isActive: true
    }];
  }
};

// Get company statistics
// Update company settings
export const updateCompanySettings = async (companyId, settings) => {
  const database = getDatabaseInstance();

  try {
    const settingsRef = ref(database, `companies/${companyId}/settings`);
    await update(settingsRef, settings);

    console.log(`Company settings updated for: ${companyId}`);
    return true;
  } catch (error) {
    console.error('Error updating company settings:', error);
    throw error;
  }
};

// Get company settings
export const getCompanySettings = async (companyId) => {
  const database = getDatabaseInstance();

  try {
    console.log('🏢 Getting settings for company:', companyId);
    const settingsRef = ref(database, `companies/${companyId}/settings`);
    const snapshot = await get(settingsRef);

    if (snapshot.exists()) {
      const settings = snapshot.val();
      console.log('🏢 Found existing settings:', settings);
      return settings;
    }

    console.log('🏢 No settings found, returning defaults');

    // Return default settings if none exist
    return {
      timezone: 'America/Los_Angeles',
      workingHours: {
        start: '09:00',
        end: '17:00'
      },
      features: {
        timeTracking: true,
        approvals: true,
        reporting: true
      },
      automaticPunches: {
        enabled: false,
        dailyDeduction: 30,
        applyToAllEmployees: true,
        excludedEmployees: [],
        effectiveDate: new Date().toISOString()
      },
      timeRounding: {
        enabled: false,
        roundingInterval: 15,
        effectiveDate: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('🏢 Error getting company settings:', error);
    console.log('🏢 Returning defaults due to error');
    // Return default settings on error
    return {
      timezone: 'America/Los_Angeles',
      workingHours: {
        start: '09:00',
        end: '17:00'
      },
      features: {
        timeTracking: true,
        approvals: true,
        reporting: true
      },
      automaticPunches: {
        enabled: false,
        dailyDeduction: 30,
        applyToAllEmployees: true,
        excludedEmployees: [],
        effectiveDate: new Date().toISOString()
      },
      timeRounding: {
        enabled: false,
        roundingInterval: 15,
        effectiveDate: new Date().toISOString()
      }
    };
  }
};

export const getCompanyStats = async (companyId) => {
  const database = getDatabaseInstance();

  try {
    // Always use company-specific paths
    const usersPath = `companies/${companyId}/users`;
    const recordsPath = `companies/${companyId}/timeRecords`;

    // Get user count
    const usersRef = ref(database, usersPath);
    const usersSnapshot = await get(usersRef);
    const userCount = usersSnapshot.exists() ? Object.keys(usersSnapshot.val()).length : 0;
    
    // Get active employees count
    let activeEmployees = 0;
    if (usersSnapshot.exists()) {
      const users = usersSnapshot.val();
      activeEmployees = Object.values(users).filter(user => 
        user.role === 'user' && user.status === 'active'
      ).length;
    }
    
    // Get time records count (last 30 days)
    const recordsRef = ref(database, recordsPath);
    const recordsSnapshot = await get(recordsRef);
    let recentRecords = 0;
    
    if (recordsSnapshot.exists()) {
      const records = recordsSnapshot.val();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      recentRecords = Object.values(records).filter(record => {
        const recordDate = new Date(record.timestamp);
        return recordDate >= thirtyDaysAgo;
      }).length;
    }
    
    return {
      totalUsers: userCount,
      activeEmployees,
      recentRecords
    };
    
  } catch (error) {
    console.error('Error getting company stats:', error);
    return {
      totalUsers: 0,
      activeEmployees: 0,
      recentRecords: 0
    };
  }
};